import { SITE_ROWS } from "./capabilities-site.generated";

import type { WidgetOfType } from "../schema/widgets";

// Canonical content of the Capabilities page tour (feature_rows#capabilities-tour).
// Every row is a What-we-do row on bisoncommerce.com: title, body and the scene
// its page mounts, in the site's order, read from ./capabilities-site.generated.ts,
// which scripts/sync-capabilities.mjs writes from the site's `main` (2026-10-26;
// until then the rows were copied here by hand). Never type row copy here:
// change the site and run `npm run sync:sources`. This file owns only what is
// the room's: the sections a supplier scrolls through (id, rail icon, eyebrow,
// title, intro), which site page fills each, the rows a section leaves out,
// and the room's bison-voice rewrites (VOICE).
//
// Scenes are the vendored site animations (src/deal-room/scenes); a scene the
// room has not vendored fails the type check of the generated file.

export type CapabilitySection = WidgetOfType<"feature_rows">["props"]["sections"][number];
type Row = CapabilitySection["rows"][number];

// Row ids are a slug of the site title, except where a stored room or a link
// already uses another id.
const ROW_IDS: Record<string, string> = {
    "marketplace-management/compliance": "listing-compliance-certificates",
};

const slug = (s: string) =>
    s
        .toLowerCase()
        .replace(/&/g, " ")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "");

// The room's bison-voice rewrites (2026-09-08 sweep: no "lever", "quietly",
// "on your side", "&"). Each applies only while the site still reads `site`;
// when the site changes that copy, the site's new text shows and
// `npm run check:sources` names the rewrite to revisit.
export const VOICE: { id: string; field: "title" | "body"; site: string; room: string }[] = [
    {
        id: "buy-box-management",
        field: "body",
        site: "Our team wins and holds the Buy Box by coordinating pricing, fulfillment, and account health together, not by pulling a single lever in isolation. We also monitor every product across all your marketplaces, catch suppressed or flagged listings as they happen, and work them until they are resolved, so your catalog stays available to buy and no sale is missed.",
        room: "Our team wins and holds the Buy Box by coordinating pricing, fulfillment, and account health together, rather than by changing one of them on its own. We also monitor every product across all your marketplaces, catch suppressed or flagged listings as they happen, and work them until they are resolved, so your catalog stays available to buy and no sale is missed.",
    },
    {
        id: "listing-suppression-prevention",
        field: "body",
        site: "Marketplaces quietly suppress a listing when its price is beaten on another channel, and a suppressed listing sells nothing. We monitor your catalog for suppressions daily, trace each one to the price that caused it, and reprice to bring the listing back, so your products stay buyable.",
        room: "Marketplaces suppress a listing when its price is beaten on another channel, and a suppressed listing sells nothing. We monitor your catalog for suppressions daily, trace each one to the price that caused it, and reprice to bring the listing back, so your products stay buyable.",
    },
    {
        id: "big-bulky-specialists",
        field: "title",
        site: "Big & bulky specialists",
        room: "Big and bulky specialists",
    },
    {
        id: "we-do-the-technical-heavy-lifting",
        field: "body",
        site: "Going live is a configuration, not a rebuild, and it never becomes an IT project on your side. You share your product data and marketplace access, then approve go-live. Our team handles everything in between: the API connection, AI-assisted attribute mapping, order validation, inventory and cancellation logic, and marketplace compliance testing.",
        room: "Going live is a configuration, not a rebuild, and it never becomes an IT project for your team. You share your product data and marketplace access, then approve go-live. Our team handles everything in between: the API connection, AI-assisted attribute mapping, order validation, inventory and cancellation logic, and marketplace compliance testing.",
    },
];

function rows(page: keyof typeof SITE_ROWS, exclude: string[] = []): Row[] {
    return SITE_ROWS[page]
        .filter((r) => !exclude.includes(r.key))
        .map((r) => {
            const id = ROW_IDS[`${page}/${r.key}`] ?? slug(r.title);
            const row: Row = { id, title: r.title, body: r.body, ...(r.scene ? { scene: r.scene } : {}) };
            for (const v of VOICE) if (v.id === id && row[v.field] === v.site) row[v.field] = v.room;
            return row;
        });
}

export const CAPABILITY_SECTIONS: CapabilitySection[] = [
    {
        id: "marketplaces",
        icon: "marketplaceMgmt",
        eyebrow: "Sell everywhere",
        title: "Marketplace management",
        intro: "One catalog, every marketplace. We list, keep in sync, and hold the Buy Box across the channels that matter.",
        // Pricing, fulfillment and support have sections of their own.
        rows: rows("marketplace-management", ["pricing", "fulfillment", "support"]),
    },
    {
        id: "pricing",
        icon: "pricingBuyBox",
        eyebrow: "Grow with technology",
        title: "Pricing automation",
        intro: "Our pricing engine reprices millions of times a day inside the rules you set: margin floors, MAP, and the regional freight reality of big-and-bulky.",
        rows: rows("pricing-automation"),
    },
    {
        id: "content",
        icon: "listingOpt",
        eyebrow: "Grow with technology",
        title: "Content optimization",
        intro: "Listings scored, rewritten, and illustrated so every product page converts the shopper it wins.",
        rows: rows("content-optimization"),
    },
    {
        id: "advertising",
        icon: "advertising",
        eyebrow: "Grow with technology",
        title: "Advertising",
        intro: "An in-house ad ops team and automation run campaigns wherever your products sell, and concentrate spend where it pays back.",
        rows: rows("advertising"),
    },
    {
        id: "fulfillment",
        icon: "handTruck",
        eyebrow: "Operate end to end",
        title: "Fulfillment and logistics",
        intro: "Negotiated carrier rates, on-time delivery, and regional ship promises built for oversized freight.",
        rows: rows("fulfillment-logistics"),
    },
    {
        id: "returns",
        icon: "cycle",
        eyebrow: "Operate end to end",
        title: "Returns",
        intro: "Returns and damages, handled end to end, so a bad moment never erodes your margin.",
        rows: rows("returns"),
    },
    {
        id: "support",
        icon: "lifeRing",
        eyebrow: "Operate end to end",
        title: "Customer support",
        intro: "One team owns every customer issue to closure, with the evidence shared back in your portal.",
        rows: rows("customer-support"),
    },
    {
        id: "white-label",
        icon: "whiteLabel",
        eyebrow: "Sell everywhere",
        title: "White label",
        intro: "You make it. We build the brand, the listings, and the demand.",
        rows: rows("white-label"),
    },
    {
        id: "storefronts",
        icon: "directChannel",
        eyebrow: "Sell everywhere",
        title: "Your own storefront",
        intro: "Bison runs category storefronts of its own, so your products reach shoppers the big marketplaces never see.",
        rows: rows("direct-sales-channels"),
    },
    {
        id: "integrations",
        icon: "integrations",
        eyebrow: "Operate end to end",
        title: "Integrations",
        intro: "One connection to Bison covers every channel, and our engineers do the technical work on both sides.",
        rows: rows("integrations"),
    },
];
